import { Button } from '../ui/button';
import { ArrowRight, Quote, User, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import exponentialGrowthBg from '@/assets/exponential-growth-bg.png';
import heroBlackMarble from '@/assets/hero-the-between.jpg';
import heroBlackMarbleMobile from '@/assets/hero-the-between-mobile.jpg';
import shaunCollett from '@/assets/shaun-collett.png';
import dimensionSelf from '@/assets/dimension-self.jpg';
import dimensionWork from '@/assets/dimension-work.jpg';
import dimensionFamily from '@/assets/dimension-family.jpg';
import dimensionLegacy from '@/assets/dimension-legacy.jpg';
import ctaCoaching from '@/assets/cta-coaching.jpg';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

// ============ Hero — unchanged structurally; pinned brand element ============
const Hero = () => (
  <section className="relative overflow-hidden flex flex-col">
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
      style={{ backgroundImage: `url(${heroBlackMarbleMobile})` }}
    />
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden sm:block"
      style={{ backgroundImage: `url(${heroBlackMarble})` }}
    />
    <div className="absolute inset-0 sm:hidden" style={{ backgroundColor: 'hsl(20 30% 6% / 0.65)' }} />
    <div className="absolute inset-0 hidden sm:block" style={{ backgroundColor: 'hsl(20 30% 6% / 0.45)' }} />
    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/60" />

    <div className="relative z-10 px-6 sm:px-8 md:px-12 lg:px-16 pt-32 sm:pt-44 md:pt-52 pb-32 sm:pb-44 md:pb-52">
      <div className="max-w-3xl mx-auto text-center space-y-8 sm:space-y-10">
        <div className="flex items-center justify-center">
          <p
            className="text-background/80 font-semibold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-[0.55rem] sm:text-xs whitespace-nowrap"
            style={{ textShadow: '0 1px 8px rgba(0,0,0,0.7)' }}
          >
            Leadership in the Age of Artificial Intelligence
          </p>
        </div>

        <h1
          className="text-background text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display leading-[1.05]"
          style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)', textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h1>

        <p
          className="text-background/90 text-2xl sm:text-3xl md:text-4xl leading-snug max-w-2xl mx-auto font-display"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.7)' }}
        >
          Being More Fully <span className="text-primary italic">Human</span>
        </p>

        <p
          className="text-background/80 text-base sm:text-lg md:text-xl leading-relaxed max-w-xl mx-auto"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.7)' }}
        >
          Executive coaching and leadership development for leaders ready to grow through continuous practice.
        </p>

      </div>
    </div>

    <div className="relative z-10 mt-auto bg-sage py-3 sm:py-5">
      <div className="container-mobile px-4 sm:px-8">
        <p className="text-center text-foreground text-[13px] sm:text-lg font-display font-semibold not-italic sm:italic sm:whitespace-nowrap tracking-tight leading-snug">
          More views. New ways. Inspired outcomes.
        </p>
      </div>
    </div>
  </section>
);

// ============ Featured Testimonial — dark editorial spread ============
const FeaturedTestimonial = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.16) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-4xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="flex items-center gap-4 mb-10">
          <span className="h-px w-12 bg-primary" aria-hidden="true" />
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
            In Their Words
          </p>
        </div>
        <Quote className="h-12 w-12 text-primary mb-8" />
        <blockquote className="space-y-8">
          <p
            className="text-2xl sm:text-3xl lg:text-4xl text-background leading-snug font-display italic"
            style={{ textWrap: 'balance' }}
          >
            I've had the life changing opportunity to study and grow alongside Kent across multiple companies and capacities. His impact is profound and expansive to life — not just work.
          </p>
          <footer className="flex items-center gap-4 pt-4">
            <img
              src={shaunCollett}
              alt="Shaun Collett"
              className="w-14 h-14 rounded-full object-cover"
            />
            <div>
              <cite className="not-italic font-semibold text-primary text-lg block">Shaun Collett</cite>
              <p className="text-background/60 text-sm">Generative and Agentic AI Leader, Amazon Web Services</p>
            </div>
          </footer>
        </blockquote>
      </div>
    </section>
  );
};

// ============ What We Believe — light editorial body ============
const WhatWeBelieve = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.10) 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              What We Believe
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Leadership is a <span className="text-primary italic">human practice.</span>
          </h2>
        </div>

        <div
          ref={body.ref}
          className={`grid md:grid-cols-12 gap-12 md:gap-16 items-start transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="md:col-span-7 space-y-7 text-lg sm:text-xl text-foreground/80 leading-relaxed font-light">
            <p>
              The technical layer is commoditizing. Every serious player is converging on the same capabilities, the same tools, the same speed. The asset that does not commoditize is the human depth required to hold complex outcomes when the ground keeps shifting.
            </p>
            <p>
              The competitive advantage of this moment belongs to those who can learn, develop, and adapt faster and deeper than anyone else. Faster horizontally, building new skills as the tools evolve. Deeper vertically, growing the inner capacity to hold complexity, make judgment calls under pressure, and let go of identities the work no longer asks for. Both. Compounding together. Most organizations know how to do the first. Few know how to do the second.
            </p>
            <p>
              In a world becoming more artificial, the edge is becoming more fully human.
            </p>
          </div>

          <aside className="md:col-span-5 md:pl-8 md:border-l md:border-border">
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-5">
              Our Through-Line
            </p>
            <p className="font-display italic text-2xl sm:text-3xl text-foreground leading-snug" style={{ textWrap: 'balance' }}>
              Creative potential lives in the <span className="text-primary">space between people.</span>
            </p>
            <div className="mt-8 flex items-center gap-4">
              <span className="h-px w-12 bg-primary/50" aria-hidden="true" />
              <p className="text-foreground/60 text-sm tracking-wide">
                Through practice. Over time.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

// ============ Four Dimensions — editorial grid with watermark numerals ============
const dimensions = [
  { title: 'Self', desc: 'Stay grounded in who you are when the pressure rises.', image: dimensionSelf },
  { title: 'Work', desc: 'Lead teams through complexity with clarity and judgment.', image: dimensionWork },
  { title: 'Family', desc: 'Be present with the people who matter most.', image: dimensionFamily },
  { title: 'Legacy', desc: 'Build something that outlasts your tenure.', image: dimensionLegacy },
];

const DimensionCard = ({
  item,
  index,
}: {
  item: (typeof dimensions)[number];
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${index * 90}ms` : '0ms' }}
      className={`group relative overflow-hidden border border-background/10 hover:border-primary/40 transition-all duration-700
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
        style={{ backgroundImage: `url(${item.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/70 to-foreground/20" />

      <div className="relative z-10 p-8 sm:p-10 pt-40 sm:pt-48 min-h-[22rem] flex flex-col justify-end">
        <div className="w-10 h-px bg-primary mb-5" aria-hidden="true" />
        <h3 className="text-background font-display text-3xl sm:text-4xl mb-3 group-hover:text-primary transition-colors duration-500">
          {item.title}
        </h3>
        <p className="text-background/75 leading-relaxed text-base">{item.desc}</p>
      </div>
    </article>
  );
};

const FourDimensions = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary/40 py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Fully Human Leadership
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
            style={{ textWrap: 'balance' }}
          >
            We develop leaders across <span className="text-primary italic">all dimensions of life.</span>
          </h2>
          <p className="text-foreground/70 text-xl leading-relaxed font-light max-w-2xl">
            You are more than your job.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-px bg-foreground/10 border border-foreground/10">
          {dimensions.map((item, i) => (
            <DimensionCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Compounding Practice — dark contemplative ============
const Compounding = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 pointer-events-none filter brightness-125 contrast-125"
        style={{ backgroundImage: `url(${exponentialGrowthBg})` }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.16) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-4xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="flex items-center gap-4 mb-10">
          <span className="h-px w-12 bg-primary" aria-hidden="true" />
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
            The ParadoxEdge Approach
          </p>
        </div>

        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-10"
          style={{ textWrap: 'balance' }}
        >
          Practice <span className="text-primary italic">compounds.</span>
        </h2>

        <p className="text-2xl sm:text-3xl text-background/85 leading-snug font-display italic mb-10" style={{ textWrap: 'balance' }}>
          Capability is built. Capacity is grown. Both compound over time.
        </p>
      </div>
    </section>
  );
};

// ============ Two Pathways — light editorial cards ============
const TwoPathways = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const cards = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 10% 20%, hsl(var(--primary) / 0.10) 0%, transparent 55%), radial-gradient(ellipse at 90% 80%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Who We Work With
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Two pathways. <span className="text-primary italic">One practice.</span>
          </h2>
        </div>

        <div
          ref={cards.ref}
          className={`grid lg:grid-cols-2 gap-6 lg:gap-8 transition-all duration-1000 delay-150 ${
            cards.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* For Individual Leaders — light/cream */}
          <article className="group relative bg-secondary border border-border p-10 sm:p-12 lg:p-14 flex flex-col overflow-hidden">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-11 h-11 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                For Individual Leaders
              </p>
            </div>
            <h3 className="text-foreground font-display text-2xl sm:text-3xl leading-snug mb-7" style={{ textWrap: 'balance' }}>
              The next level of impact asks for a different level of being.
            </h3>
            <div className="space-y-5 text-foreground/75 leading-relaxed text-base sm:text-lg font-light flex-1">
              <p>
                You have built real capability and earned real results. The next level will not come from more of what you already know.
              </p>
              <p>
                It comes from leading with presence under pressure, sitting with uncertainty, and making the calls only a human can make.
              </p>
            </div>
            <div className="mt-10 pt-8 border-t border-foreground/15">
              <Link
                to="/services"
                onClick={() => window.scrollTo(0, 0)}
                className="group/link inline-flex items-center gap-3 text-foreground font-display text-lg hover:text-primary transition-colors"
              >
                <span className="border-b border-foreground/30 group-hover/link:border-primary pb-1 transition-colors">
                  Explore for Leaders
                </span>
                <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </article>

          {/* For Organizations — dark inverted */}
          <article className="group relative bg-foreground text-background border border-foreground p-10 sm:p-12 lg:p-14 flex flex-col overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none opacity-80"
              style={{
                background:
                  'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.20) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
              }}
              aria-hidden="true"
            />
            <div className="relative z-10 flex items-center gap-4 mb-8">
              <div className="w-11 h-11 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                For Organizations
              </p>
            </div>
            <h3 className="relative z-10 text-background font-display text-2xl sm:text-3xl leading-snug mb-7" style={{ textWrap: 'balance' }}>
              What the work asks of leaders now is developmental.
            </h3>
            <div className="relative z-10 space-y-5 text-background/75 leading-relaxed text-base sm:text-lg font-light flex-1">
              <p>
                AI is rewriting every operating model at a pace that does not let up. Technical fluency alone is no longer enough.
              </p>
              <p>
                Leaders need the capacity to regulate under pressure, hold uncertainty, and generate through complexity together.
              </p>
            </div>
            <div className="relative z-10 mt-10 pt-8 border-t border-background/20">
              <Link
                to="/for-organizations"
                onClick={() => window.scrollTo(0, 0)}
                className="group/link inline-flex items-center gap-3 text-background font-display text-lg hover:text-primary transition-colors"
              >
                <span className="border-b border-background/30 group-hover/link:border-primary pb-1 transition-colors">
                  Explore for Organizations
                </span>
                <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

// ============ Final CTA — dark with hero image ============
const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${ctaCoaching})` }}
      />
      <div className="absolute inset-0 bg-foreground/85" />
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-light max-w-xl mx-auto">
          One conversation is where the practice begins.
        </p>
        <div className="pt-2">
          <Button
            onClick={handleScheduleCall}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
          >
            Begin a Conversation
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const HomeHeroSection = () => (
  <div>
    <Hero />
    <FeaturedTestimonial />
    <WhatWeBelieve />
    <FourDimensions />
    <Compounding />
    <TwoPathways />
    <FinalCTA />
  </div>
);

export default HomeHeroSection;
